export function applyMultiScorecardResponse(result, setters, options = {}) {
  if (!result?.columns || !setters) return;

  const {
    setDataBar,
    setRegimeBar,
    setRegimeBarForNocrash,
    setYearsList,
    setBins,
    setMinMaxAvgArray,
    setMinMaxAvgArrayNoCrash,
    setMinAssetArray,
    setMaxAssetArray,
    setAverageAssetArray,
    setMinAssetArrayNocrash,
    setMaxAssetArrayNocrash,
    setAverageAssetArrayNocrash,
    setMinAssetReshuffleArray,
    setMaxAssetReshuffleArray,
    setAverageAssetReshuffleArray,
    setMixMinAssetArray,
    setMixMaxAssetArray,
    setMixAvgAssetArray,
    setMixMinAssetReshuffleArray,
    setMixMaxAssetReshuffleArray,
    setMixAvgAssetReshuffleArray,
    setMinMixNoCrash,
    setMaxMixNoCrash,
    setAvgMixNoCrash,
    setAm,
    setAmNocrash,
    setGm,
    setGmNocrash,
    setAmMean,
    setAmMeanNocrash,
    setAmMeanReshuffle,
    setMixAM,
    setMixGM,
    setMixAMReshuffle,
    setMixGMReshuffle,
    setAmMixNoCrash,
    setGmMixNoCrash,
    setPercentile2DataMaxAllocation,
    setChart5ByMode,
    setLoading,
  } = setters;

  const regime = result.regime || {};
  if (setDataBar && regime.dataBar) setDataBar(regime.dataBar);
  if (setRegimeBar && regime.regimeBar) setRegimeBar(regime.regimeBar);
  if (setRegimeBarForNocrash && regime.regimeBarForNocrash) {
    setRegimeBarForNocrash(regime.regimeBarForNocrash);
  }
  if (setYearsList && regime.yearsList) setYearsList(regime.yearsList);
  if (setBins && regime.bins) setBins(regime.bins);

  const columnEntries = Object.entries(result.columns || {});
  if (columnEntries.length === 0) {
    setLoading?.(false);
    return;
  }

  const firstModes = columnEntries[0][1]?.modes || {};
  const normalBase = firstModes.normal?.base;
  const nocrashBase = firstModes.nocrash?.base;

  if (normalBase) {
    setMinMaxAvgArray?.({
      minArray: normalBase.min || [],
      maxArray: normalBase.max || [],
      averageArray: normalBase.avg || [],
    });
    setAm?.(normalBase.am ?? 0);
    setGm?.(normalBase.gm ?? 0);
  }

  if (nocrashBase) {
    setMinMaxAvgArrayNoCrash?.({
      minArrayNocrash: nocrashBase.min || [],
      maxArrayNocrash: nocrashBase.max || [],
      averageArrayNocrash: nocrashBase.avg || [],
    });
    setAmNocrash?.(nocrashBase.am ?? 0);
    setGmNocrash?.(nocrashBase.gm ?? 0);
  }

  const nextNormalMin = {};
  const nextNormalMax = {};
  const nextNormalAvg = {};
  const nextNoCrashMin = {};
  const nextNoCrashMax = {};
  const nextNoCrashAvg = {};
  const nextReshuffleMin = {};
  const nextReshuffleMax = {};
  const nextReshuffleAvg = {};
  const nextMixMin = {};
  const nextMixMax = {};
  const nextMixAvg = {};
  const nextMixReshuffleMin = {};
  const nextMixReshuffleMax = {};
  const nextMixReshuffleAvg = {};
  const nextMixNoCrashMin = {};
  const nextMixNoCrashMax = {};
  const nextMixNoCrashAvg = {};
  const nextAmMean = {};
  const nextMixAM = {};
  const nextMixGM = {};
  const nextMixAMReshuffle = {};
  const nextMixGMReshuffle = {};
  const nextAmMixNoCrash = {};
  const nextGmMixNoCrash = {};
  const nextRecommended = {};

  let sharedAmMeanReshuffle = 0;
  let sharedAmMeanNocrash = 0;

  columnEntries.forEach(([columnId, columnValue]) => {
    const modes = columnValue?.modes || {};
    const normal = modes.normal;
    const reshuffle = modes.reshuffle;
    const nocrash = modes.nocrash;

    if (normal) {
      nextNormalMin[columnId] = normal.other?.min || [];
      nextNormalMax[columnId] = normal.other?.max || [];
      nextNormalAvg[columnId] = normal.other?.avg || [];
      nextMixMin[columnId] = normal.mix?.min || [];
      nextMixMax[columnId] = normal.mix?.max || [];
      nextMixAvg[columnId] = normal.mix?.avg || [];
      nextAmMean[columnId] = normal.other?.am ?? 0;
      nextMixAM[columnId] = normal.mix?.am ?? 0;
      nextMixGM[columnId] = normal.mix?.gm ?? 0;
    }

    if (reshuffle) {
      nextReshuffleMin[columnId] = reshuffle.other?.min || [];
      nextReshuffleMax[columnId] = reshuffle.other?.max || [];
      nextReshuffleAvg[columnId] = reshuffle.other?.avg || [];
      nextMixReshuffleMin[columnId] = reshuffle.mix?.min || [];
      nextMixReshuffleMax[columnId] = reshuffle.mix?.max || [];
      nextMixReshuffleAvg[columnId] = reshuffle.mix?.avg || [];
      nextMixAMReshuffle[columnId] = reshuffle.mix?.am ?? 0;
      nextMixGMReshuffle[columnId] = reshuffle.mix?.gm ?? 0;
      if (!sharedAmMeanReshuffle) {
        sharedAmMeanReshuffle = reshuffle.other?.am ?? 0;
      }
    }

    if (nocrash) {
      nextNoCrashMin[columnId] = nocrash.other?.min || [];
      nextNoCrashMax[columnId] = nocrash.other?.max || [];
      nextNoCrashAvg[columnId] = nocrash.other?.avg || [];
      nextMixNoCrashMin[columnId] = nocrash.mix?.min || [];
      nextMixNoCrashMax[columnId] = nocrash.mix?.max || [];
      nextMixNoCrashAvg[columnId] = nocrash.mix?.avg || [];
      nextAmMixNoCrash[columnId] = nocrash.mix?.am ?? 0;
      nextGmMixNoCrash[columnId] = nocrash.mix?.gm ?? 0;
      if (!sharedAmMeanNocrash) {
        sharedAmMeanNocrash = nocrash.other?.am ?? 0;
      }
    }

    const activeMode = options.mode && modes[options.mode] ? modes[options.mode] : null;
    if (activeMode?.chart5?.recommendedAlloc != null) {
      nextRecommended[columnId] = activeMode.chart5.recommendedAlloc;
    }
  });

  setMinAssetArray?.(nextNormalMin);
  setMaxAssetArray?.(nextNormalMax);
  setAverageAssetArray?.(nextNormalAvg);
  setMinAssetArrayNocrash?.(nextNoCrashMin);
  setMaxAssetArrayNocrash?.(nextNoCrashMax);
  setAverageAssetArrayNocrash?.(nextNoCrashAvg);
  setMinAssetReshuffleArray?.(nextReshuffleMin);
  setMaxAssetReshuffleArray?.(nextReshuffleMax);
  setAverageAssetReshuffleArray?.(nextReshuffleAvg);
  setMixMinAssetArray?.(nextMixMin);
  setMixMaxAssetArray?.(nextMixMax);
  setMixAvgAssetArray?.(nextMixAvg);
  setMixMinAssetReshuffleArray?.(nextMixReshuffleMin);
  setMixMaxAssetReshuffleArray?.(nextMixReshuffleMax);
  setMixAvgAssetReshuffleArray?.(nextMixReshuffleAvg);
  setMinMixNoCrash?.(nextMixNoCrashMin);
  setMaxMixNoCrash?.(nextMixNoCrashMax);
  setAvgMixNoCrash?.(nextMixNoCrashAvg);
  setAmMean?.(nextAmMean);
  setAmMeanReshuffle?.(sharedAmMeanReshuffle);
  setAmMeanNocrash?.(sharedAmMeanNocrash);
  setMixAM?.(nextMixAM);
  setMixGM?.(nextMixGM);
  setMixAMReshuffle?.(nextMixAMReshuffle);
  setMixGMReshuffle?.(nextMixGMReshuffle);
  setAmMixNoCrash?.(nextAmMixNoCrash);
  setGmMixNoCrash?.(nextGmMixNoCrash);
  setPercentile2DataMaxAllocation?.((prev) => ({ ...prev, ...nextRecommended }));

  if (setChart5ByMode) {
    setChart5ByMode((prev) => {
      const next = { ...(prev || {}) };
      columnEntries.forEach(([columnId, columnValue]) => {
        const modes = columnValue?.modes || {};
        next[columnId] = {
          ...(next[columnId] || {}),
          ...(modes.normal?.chart5 ? { normal: modes.normal.chart5 } : {}),
          ...(modes.reshuffle?.chart5 ? { reshuffle: modes.reshuffle.chart5 } : {}),
          ...(modes.nocrash?.chart5 ? { nocrash: modes.nocrash.chart5 } : {}),
        };
      });
      return next;
    });
  }

  setLoading?.(false);
}
